/*
 * Copyright (c) 2023, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */

import loadable from '@loadable/component'
import {getConfig} from '@salesforce/pwa-kit-runtime/utils/ssr-config'
import React, {useEffect} from 'react'
import {withRouter} from 'react-router-dom'

// Components
import {Skeleton} from '@salesforce/retail-react-app/app/components/shared/ui'
import {routes as _routes} from '@salesforce/retail-react-app/app/routes'
import {configureRoutes} from '@salesforce/retail-react-app/app/utils/routes-utils'

// extensible
import useMultiSite from '@salesforce/retail-react-app/app/hooks/use-multi-site'
import ChangePassword from './pages/change-password'

import '@adyen/adyen-salesforce-pwa/dist/app/adyen.css'
import {originType} from './constants/plp.constants'

const DEFAULT_SITE_ID = 'rennai'

const fallback = <Skeleton height="75vh" width="100%" />

// Pages
const onClient = typeof window !== 'undefined'
const Home = loadable(() => import('./pages/home'), {fallback})
const Account = loadable(() => import('./pages/account'), {fallback})
const Ingredients = loadable(() => import('./pages/ingredients'), {fallback})
const Brands = loadable(() => import('./pages/brands'), {fallback})
const BrandPage = loadable(() => import('./pages/brands/brand'), {fallback})
const EventsPage = loadable(() => import('./pages/events'), {fallback})
const BooxiPage = loadable(() => import('./pages/booxi'), {fallback})
const BooxiBookingPage = loadable(() => import('./pages/booxi/booking'), {
    fallback
})
const BlogArticlePage = loadable(() => import('./pages/blog/blog-detail'), {
    fallback
})
const ResetPassword = loadable(() => import('./pages/reset-password'), {
    fallback
})
const Blog = loadable(() => import('./pages/blog'), {
    fallback
})
const ContactPage = loadable(() => import('./pages/contact'), {
    fallback
})
const LocationPage = loadable(() => import('./pages/location'), {
    fallback
})
const StaticTemplate = loadable(() => import('./pages/static-template'), {
    fallback
})
const ProductDetail = loadable(() => import('./pages/product-detail'), {
    fallback
})
const PageNotFound = loadable(() => import('./pages/page-not-found'), {
    fallback
})
const Login = loadable(() => import('./pages/login'), {
    fallback
})
const Registration = loadable(() => import('./pages/registration'), {
    fallback
})
const StoreRegistration = loadable(() => import('./pages/store-registration'), {
    fallback
})
const StorePasswordReset = loadable(() => import('./pages/store/password'), {
    fallback
})
const Collection = loadable(() => import('./pages/collection'), {
    fallback
})
const ProductList = loadable(() => import('./pages/product-list'), {
    fallback
})
const Cart = loadable(() => import('./pages/cart'), {
    fallback
})
const Wishlist = loadable(() => import('./pages/account/wishlist'), {
    fallback
})
const Checkout = loadable(() => import('./pages/checkout'), {
    fallback
})
const AboutTest = loadable(() => import('./pages/about'), {
    fallback
})
const Careers = loadable(() => import('./pages/careers'), {
    fallback
})
const NewsletterPage = loadable(() => import('./pages/newsletter'), {
    fallback
})
const MyPage = loadable(() => import('./pages/my-page'), {
    fallback
})
const RegistrationAccessPage = loadable(
    () => import('./pages/registration-access'),
    {
        fallback
    }
)
const CheckoutConfirmation = loadable(
    () => import('./pages/checkout/confirmation'),
    {fallback}
)
// Checkout Redirect page from Adyen
const AdyenCheckoutRedirect = loadable(
    () => import('./pages/checkout/redirect'),
    {
        fallback: fallback
    }
)

// Checkout Error page from Adyen
const AdyenCheckoutError = loadable(() => import('./pages/checkout/error'), {
    fallback: fallback
})

// Remove handling SFRA routes on PWA Kit
const SFRARoutes = ['*']

const routes = [
    {
        path: '/product/:productId',
        component: ProductDetail
    },
    {
        path: '/pages/ingredients',
        component: Ingredients
    },
    {
        path: '/pages/brands',
        component: Brands
    },
    {
        path: '/brand/:slug',
        component: BrandPage
    },
    {
        path: '/events',
        component: EventsPage
    },
    {
        path: '/pages/about',
        component: AboutTest
    },
    {
        path: '/pages/careers',
        component: Careers
    },
    {
        path: '/pages/blog/:slug',
        component: BlogArticlePage
    },
    {
        path: '/account',
        component: Account
    },
    {
        path: '/',
        component: Home,
        exact: true
    },
    {
        path: '/reset-password',
        component: ResetPassword,
        exact: true
    },
    {
        path: '/change-password',
        component: ChangePassword,
        exact: true
    },
    {
        path: '/login',
        component: Login,
        exact: true
    },
    {
        path: '/store/registration',
        component: StoreRegistration,
        exact: true
    },
    {
        path: '/store/password',
        component: StorePasswordReset,
        exact: true
    },
    {
        path: '/registration',
        component: Registration,
        exact: true
    },
    {
        path: '/cart',
        component: Cart,
        exact: true
    },
    {
        path: '/checkout',
        component: Checkout,
        exact: true
    },
    {
        path: '/checkout/redirect',
        component: AdyenCheckoutRedirect,
        exact: true
    },
    {
        path: '/checkout/error',
        component: AdyenCheckoutError,
        exact: true
    },
    {
        path: '/checkout/confirmation/:orderNo',
        component: CheckoutConfirmation,
        exact: true
    },
    {
        path: '/wishlist',
        component: Wishlist
    },
    {
        path: '/shop-all',
        component: (props) => (
            <ProductList {...props} origin={originType.shopAll} />
        )
    },
    {
        path: '/sale',
        component: (props) => (
            <ProductList {...props} origin={originType.sale} />
        )
    },
    {
        path: '/search',
        component: (props) => (
            <ProductList {...props} origin={originType.search} />
        )
    },
    {
        path: '/category/:categoryId',
        component: (props) => <Collection {...props} />
    },
    {
        path: '/pages/blogs',
        component: Blog,
        exact: true
    },
    {
        path: '/pages/contact',
        component: ContactPage,
        exact: true
    },
    {
        path: '/pages/location',
        component: LocationPage,
        exact: true
    },
    {
        path: '/pages/:slug',
        component: StaticTemplate,
        exact: true
    },
    {
        path: '/services',
        component: BooxiPage,
        exact: true
    },
    {
        path: '/services/:tag',
        component: BooxiPage,
        exact: true
    },
    {
        path: '/services/:tag/:service',
        component: BooxiPage,
        exact: true
    },
    {
        path: '/services/:tag/:service/:person',
        component: BooxiPage,
        exact: true
    },
    {
        path: '/service/:bookingId',
        component: BooxiBookingPage,
        exact: true
    },
    {
        path: '/page-not-found',
        component: PageNotFound
    },
    {
        path: '/newsletter',
        component: NewsletterPage,
        exact: true
    },
    {
        path: '/registration-access',
        component: RegistrationAccessPage,
        exact: true
    },
    {
        path: '/my-rennai',
        component: MyPage,
        exact: true
    },
    ..._routes.filter((route) => !SFRARoutes.includes(route.path)),
    {
        path: '*',
        component: withRouter((props) => {
            const {location} = props
            const urlParams = new URLSearchParams(location.search)
            const {site} = useMultiSite()
            const siteId = site && site.id ? site.id : DEFAULT_SITE_ID
            useEffect(() => {
                const newURL = new URL(window.location)
                const pathname = window.location.pathname
                if (
                    !urlParams.has('redirected') &&
                    !pathname.includes('sitemap')
                ) {
                    newURL.searchParams.append('redirected', '1')
                    newURL.pathname = `/s/${siteId}/${pathname
                        .split('/')
                        .slice(2)
                        .join('/')}`
                    window.location.replace(newURL)
                }
            }, [onClient && window.location.href])
            if (urlParams.has('redirected')) {
                return <PageNotFound {...props} />
            }
            return null
        })
    }
]

export default () => {
    const config = getConfig()
    return configureRoutes(routes, config, {
        ignoredRoutes: ['/callback', '*']
    })
}
